import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Navigation, UserCheck, Flag, Phone, ArrowLeft, CheckCircle2, Banknote, Clock, Loader2, XCircle, Route, Edit3, UserX,
} from "lucide-react";
import ChatBox from "@/components/ChatBox";
import PaymentQRCard from "@/components/PaymentQRCard";
import DriverPaymentCard from "@/components/DriverPaymentCard";
import ModifyDestinationDialog from "@/components/ModifyDestinationDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import SlideToConfirm from "@/components/SlideToConfirm";
import { launchGps } from "@/lib/gpsPref";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_LABEL = {
  accepted: "Acceptée",
  picked_up: "Client pris en charge",
  in_progress: "Trajet en cours",
  completed: "Terminée",
};
const ORDER = ["accepted", "picked_up", "in_progress", "completed"];

/** Open the driver's preferred GPS app (Google / Waze / Apple Plans).
 * Uses `<a>.click()` under the hood — fires inside a user gesture and
 * survives popup blockers on iOS PWA mode. Coordinates are passed when
 * available so Waze/Apple don't fall back to weaker address parsing. */
const launchGoogleMapsTo = (address, coord) => {
  if (!address && !coord) return;
  launchGps(address, coord);
};

/**
 * Dedicated fullscreen page for the driver's current active ride.
 * Shows the single active booking (status in accepted/picked_up/in_progress)
 * with every action: GPS, call, chat, payment confirmation, completion.
 */
export default function DriverActiveRide() {
  const nav = useNavigate();
  const { profile } = useAuth();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);

  const driverName = profile ? `${profile.first_name} ${profile.last_name || ""}`.trim() : "Chauffeur";

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/bookings/mine");
      const active = (data.bookings || []).find((b) => ["accepted", "picked_up", "in_progress"].includes(b.status));
      setRide(active || null);
      if (!active) {
        // No active ride — send the driver back to their dashboard
        nav("/dashboard/driver", { replace: true });
      }
    } catch (e) {
      // ignore transient errors
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { refresh(); }, [refresh]);

  // Tight poll: 5 s to see payment confirmation come in quickly
  useEffect(() => {
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  // Rating flow — after completion we navigate to /dashboard/driver
  // with router state so the dialog is rendered on the dashboard (not
  // here — otherwise the 5 s refresh poll can pull us back to a
  // different active ride mid-rating). No local state needed anymore.

  const setStatus = async (new_status) => {
    if (!ride) return;
    // Capture the ride BEFORE the state mutates so we can open the rating
    // dialog with the correct target name even after the poll clears
    // `ride`.
    const snapshot = ride;
    try {
      await api.post(`/driver/bookings/${ride.id}/status?new_status=${new_status}`);
      if (new_status === "picked_up") { toast.success("Prise en charge — GPS vers la destination"); launchGoogleMapsTo(ride.arrival_address); }
      else if (new_status === "completed") {
        toast.success("Course terminée");
        // Clear the local ride so the "in_progress" screen disappears
        // *and* immediately navigate to the driver dashboard, passing
        // the completed booking snapshot via router state so the rating
        // dialog is rendered on a stable page (not on this one, which
        // could otherwise get re-populated by the 5 s refresh poll
        // finding another active ride — that's exactly what caused
        // "ça me remet sur la page où il y a le slide").
        setRide(null);
        nav("/dashboard/driver", { replace: true, state: { rateBooking: snapshot } });
        return;
      }
      else toast.success("Statut mis à jour");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  /** Pressed after pickup: single action that launches GPS AND flips the ride
   * status to `in_progress`. This replaces the two previous buttons
   * ("GPS vers la destination" + "En course"). L'ordre est important :
   *   1. On flippe le statut EN PREMIER de manière optimiste (ride local
   *      passe en "in_progress" pour que l'UI mette immédiatement à jour
   *      l'écran — évite l'impression de "rien ne se passe" quand iOS
   *      ouvre Maps en background pendant plusieurs secondes).
   *   2. On lance Google Maps dans le même geste utilisateur (sans quoi
   *      Safari bloque le deep-link).
   *   3. On envoie l'API POST en fire-and-forget avec rollback si erreur. */
  const startTripToDestination = () => {
    if (!ride) return;
    // 1. Optimistic UI — flip local ride state immediately.
    setRide((r) => (r ? { ...r, status: "in_progress" } : r));
    // 2. Deep-link Maps synchronously so iOS keeps the gesture context.
    try { launchGoogleMapsTo(ride.arrival_address, ride.arrival_coord || ride.destination_coord); } catch (_) { /* ignore */ }
    // 3. Persist to backend in the background; rollback UI if it fails.
    api.post(`/driver/bookings/${ride.id}/status?new_status=in_progress`)
      .then(() => { toast.success("En route vers la destination"); refresh(); })
      .catch((e) => {
        // Rollback to picked_up so the slide comes back into view.
        setRide((r) => (r ? { ...r, status: "picked_up" } : r));
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      });
  };

  const confirmCash = async () => {
    if (!ride) return;
    try {
      await api.post(`/driver/bookings/${ride.id}/confirm-cash`);
      toast.success("Paiement en espèces confirmé");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const [cashConfirmOpen, setCashConfirmOpen] = useState(false);
  const askConfirmCash = () => setCashConfirmOpen(true);
  const handleCashConfirmed = async () => {
    setCashConfirmOpen(false);
    await confirmCash();
  };

  const [confirmRelease, setConfirmRelease] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [modifyDestOpen, setModifyDestOpen] = useState(false);

  // ── Geofence : pickup ET destination ──────────────────────────────────
  // L'objectif : auto-flipper le statut "picked_up" dès que le chauffeur
  // arrive à l'hôtel (<50m) puis activer le slide « Terminer la course »
  // dès que l'arrivée est atteinte. Le suivi GPS tourne en permanence
  // tant qu'une course est active.
  const PICKUP_RADIUS_M = 150;
  const DROPOFF_RADIUS_M = 50;
  const [driverPos, setDriverPos] = useState(null);
  const [gpsBlocked, setGpsBlocked] = useState(false);
  useEffect(() => {
    if (!ride || !["accepted", "picked_up", "in_progress"].includes(ride.status)) return;
    if (!navigator.geolocation) { setGpsBlocked(true); return; }
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsBlocked(false);
        setDriverPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => {
        if (err && (err.code === 1 || err.code === 2)) setGpsBlocked(true);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => { try { navigator.geolocation.clearWatch(id); } catch (_) {} };
  }, [ride?.status]);

  // Haversine distance (m)
  const haversineM = (a, b) => {
    if (!a || !b) return null;
    if (a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null;
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(x));
  };
  const distanceToPickup = ride?.hotel_coord ? haversineM(driverPos, ride.hotel_coord) : null;
  // Si le booking n'a pas de hotel_coord (ancien dataset), on n'applique
  // pas la garde — le chauffeur peut prendre en charge normalement.
  const hasPickupCoord = !!(ride?.hotel_coord?.lat && ride?.hotel_coord?.lng);
  const atPickup = !hasPickupCoord
    ? true
    : distanceToPickup !== null && distanceToPickup <= PICKUP_RADIUS_M;

  // Géocodage / distance vers la destination (cache côté serveur ou calcul ad hoc)
  const dropoffCoord = ride?.arrival_coord || ride?.destination_coord || null;
  const distanceToDropoff = dropoffCoord ? haversineM(driverPos, dropoffCoord) : null;
  const hasDropoffCoord = !!(dropoffCoord?.lat && dropoffCoord?.lng);
  const atDropoff = !hasDropoffCoord
    ? true  // fallback si pas de coord (legacy) — on autorise quand même
    : distanceToDropoff !== null && distanceToDropoff <= DROPOFF_RADIUS_M;

  // Auto-flip "accepted → picked_up" dès qu'on est sur place.
  // Évite au chauffeur de cliquer manuellement sur Prise en charge.
  useEffect(() => {
    if (!ride || ride.status !== "accepted") return;
    if (!atPickup) return;
    // Block auto-pickup for scheduled rides not on the planned day
    const isScheduled = (ride.booking_type || "immediate") === "scheduled" && ride.scheduled_at;
    if (isScheduled) {
      const a = new Date(ride.scheduled_at), b = new Date();
      const sameDay = a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
      if (!sameDay) return;
    }
    let cancelled = false;
    (async () => {
      try {
        await api.post(`/driver/bookings/${ride.id}/status?new_status=picked_up`);
        if (!cancelled) {
          toast.success("Arrivée détectée — client notifié");
          if (navigator.vibrate) navigator.vibrate([60, 30, 60]);
          // Note: GPS to destination is NOT launched here (step 2).
          // It will be launched in step 3 when the driver slides
          // "Commencer la course" (so the launch fires inside a real
          // user gesture and is never blocked by popup heuristics).
          refresh();
        }
      } catch (_) { /* silent — sera réessayé au prochain refresh */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ride?.id, ride?.status, atPickup]);
  const releaseRide = async () => {
    if (!ride) return;
    setReleasing(true);
    try {
      await api.post(`/driver/bookings/${ride.id}/release`);
      toast.success("Course annulée — un autre chauffeur va être notifié");
      setConfirmRelease(false);
      nav("/dashboard/driver", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setReleasing(false); }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Chargement de votre course…</p>
      </div>
    );
  }
  if (!ride) return null;

  const idx = ORDER.indexOf(ride.status);
  // Paid = paiement finalisé côté serveur ("paid") OU pré-autorisé Apple
  // Pay / Google Pay natif ("authorized" — fonds bloqués sur la carte).
  // Dans les 2 cas le client N'A RIEN à sortir en voiture donc on
  // affiche le badge "Déjà payée" et on désactive les boutons "Cash /
  // Tap-to-Pay" pour éviter les doubles paiements.
  const paid = ride.payment_status === "paid" || ride.payment_status === "authorized" || !!ride.payment_authorized_at;
  const cashChosen = ride.payment_method === "cash";
  // Le client a réglé par carte (Apple Pay / Google Pay / carte 1-tap) —
  // aucune collecte à faire côté chauffeur. Utilisé pour masquer les
  // encaissements physiques (bouton "Espèces reçu" / "Tap-to-Pay") et
  // dégoupiller le slide "Terminer la course" (plus de blocage sur
  // `payment_status !== paid`).
  const clientPrepaidByCard = (ride.payment_method === "card" || ride.payment_method === "prepaid") && paid;

  // For scheduled rides, block "Prise en charge" until the calendar day of
  // `scheduled_at` actually arrives — otherwise a driver could pick up a
  // client hours or days ahead of time.
  const isScheduled = (ride.booking_type || "immediate") === "scheduled" && ride.scheduled_at;
  const sameLocalDay = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  const pickupBlocked = isScheduled && !sameLocalDay(new Date(ride.scheduled_at), new Date());
  const scheduledDayLabel = isScheduled ? new Date(ride.scheduled_at).toLocaleDateString("fr-FR", {
    weekday: "long", day: "2-digit", month: "long",
  }) : "";

  return (
    <div className="mx-auto max-w-3xl px-4 pb-6 safe-top sm:px-6 sm:pb-10" data-testid="driver-active-page">
      <div className="flex items-center justify-between gap-2">
        <Link to="/dashboard/driver" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="back-to-dashboard">
          <ArrowLeft className="h-3.5 w-3.5" /> Retour au dashboard
        </Link>
        <div className="flex items-center gap-2">
          {paid && (
            <Badge
              className="bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]"
              data-testid="header-paid-badge"
            >
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Déjà payée
            </Badge>
          )}
          <Badge className="bg-primary text-primary-foreground">{STATUS_LABEL[ride.status]}</Badge>
        </div>
      </div>

      {/* Ride identity */}
      <div className="mt-6 rounded-3xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-card to-card p-6 shadow-sm sm:p-8">
        <p className="text-xs uppercase tracking-widest text-primary">Course actuelle</p>
        <h1 className="mt-1 font-[Outfit] text-3xl font-normal tracking-tight">{ride.client_name}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{ride.passenger_count} pers · {ride.luggage_count} bagages</p>

        {ride.is_multi_stop && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
            data-testid="active-multi-stop-badge"
          >
            <Route className="h-3.5 w-3.5" />
            Course multi-arrêts · {(ride.stops || []).length} arrêt(s) intermédiaire(s)
          </div>
        )}

        <div className="mt-5 space-y-2 text-sm">
          <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 flex-none text-primary" /> <span><strong>Départ :</strong> {ride.departure_address}</span></p>
          {(ride.stops || []).map((s) => (
            <p
              key={s.order}
              className="flex items-start gap-2 pl-1"
              data-testid={`active-stop-${s.order}`}
            >
              <Route className="mt-0.5 h-4 w-4 flex-none text-primary/70" />
              <span className="text-foreground">
                <strong>Arrêt {s.order} :</strong> {s.address}{" "}
                <button
                  type="button"
                  onClick={() => launchGoogleMapsTo(s.address)}
                  className="ml-1 inline-flex items-center gap-0.5 text-[11px] text-primary underline-offset-2 hover:underline"
                  data-testid={`active-stop-gps-${s.order}`}
                >
                  <Navigation className="h-3 w-3" /> GPS
                </button>
              </span>
            </p>
          ))}
          <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 flex-none text-accent" /> <span><strong>Arrivée :</strong> {ride.arrival_address}</span></p>
        </div>

        {/* Progress strip */}
        <div className="mt-6 flex items-center gap-1.5">
          {ORDER.map((s, i) => (
            <span key={s} className={`h-1.5 flex-1 rounded-full ${idx >= i ? "bg-primary" : "bg-border"}`} />
          ))}
        </div>
      </div>

      {/* Payment block — placed RIGHT BELOW the ride identity so the driver
          sees the payment status (paid / Square / cash) the moment the page
          opens, with zero scroll required. Critical UX to prevent double
          billing (already-paid rides) and missed encaissement (Square
          button must be unmissable). */}
      <div className="mt-5" data-testid="active-payment-block">
        {paid ? (
          /* Big, unmissable banner so the driver KNOWS not to ask the
             client to pay again. Success green + large CheckCircle + bold
             price + contextual subtitle. */
          <div
            className="rounded-3xl border-2 border-[hsl(var(--success))] bg-[hsl(var(--success))]/10 p-6 shadow-[0_8px_24px_-8px_hsl(var(--success)/0.35)]"
            data-testid="active-paid"
          >
            <div className="flex items-start gap-4">
              <div className="grid h-14 w-14 flex-none place-items-center rounded-full bg-[hsl(var(--success))] text-white">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--success))]">
                  Course déjà payée
                </p>
                <h2 className="mt-1 font-[Outfit] text-2xl font-semibold tracking-tight text-foreground">
                  {ride.price} € · réglés
                </h2>
                <p className="mt-1 text-sm text-foreground/80">
                  {cashChosen
                    ? "Paiement en espèces déjà confirmé. N'encaissez rien de plus."
                    : "Le client a payé d'avance par Apple Pay / carte. N'encaissez rien à bord."}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="active-driver-collect">
            <DriverPaymentCard booking={ride} onPaid={refresh} />
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-secondary/40 px-5 py-4" data-testid="active-cash-fallback">
              <div className="min-w-0">
                <p className="text-sm font-medium flex items-center gap-2"><Banknote className="h-4 w-4 text-muted-foreground" /> Encaissement en espèces</p>
                <p className="text-xs text-muted-foreground">Si le client paye en liquide, confirmez ici après réception.</p>
              </div>
              <Button onClick={cashChosen ? confirmCash : askConfirmCash} variant={cashChosen ? "default" : "outline"} size="sm" className={`rounded-full gap-1.5 ${cashChosen ? "bg-[hsl(var(--warning))] text-white hover:bg-[hsl(var(--warning))]/90" : ""}`} data-testid="active-confirm-cash">
                <Banknote className="h-4 w-4" /> {cashChosen ? "J'ai reçu l'argent" : "Espèces reçu"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Primary action panel — clean 5-step flow:
          1. accepted          → "GPS direction client" (button)
          2. accepted (atPickup) → auto-flip to picked_up + notif client
          3. picked_up         → slide "Commencer la course" (flips to in_progress + launches GPS)
          4. in_progress       → "GPS destination" fallback button (re-launch)
          5. in_progress (paid + atDropoff) → slide "Terminer la course" */}
      <div className="mt-5 space-y-3">
        {ride.status === "accepted" && (
          <>
            {/* Step 1 — GPS direction client */}
            <Button onClick={() => launchGoogleMapsTo(ride.departure_address, ride.hotel_coord)} className="w-full gap-1.5 rounded-full bg-primary hover:bg-primary/90" data-testid="active-gps-hotel">
              <Navigation className="h-4 w-4" /> GPS direction client
            </Button>
            {pickupBlocked ? (
              <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-4 py-3 text-xs text-[hsl(var(--warning))]" data-testid="pickup-blocked-notice">
                🕒 Cette course est programmée pour le <strong className="capitalize">{scheduledDayLabel}</strong>. La prise en charge sera disponible ce jour-là.
              </div>
            ) : (
              <>
                {/* Step 2 — auto-detect arrival */}
                <div className="rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-3 text-center text-xs text-muted-foreground" data-testid="pickup-waiting">
                  {atPickup
                    ? "📍 Arrivée détectée — le client est notifié…"
                    : gpsBlocked
                      ? "Activez votre GPS pour la détection automatique d'arrivée"
                      : "🚗 En route vers le client…"}
                </div>
                {/* Manual fallback — confirm arrival at pickup if auto-detection
                    fails (GPS denied, coords missing, etc.). Sends notif via
                    backend status flip; does NOT launch destination GPS yet
                    (that happens in step 3). */}
                <Button
                  type="button"
                  onClick={() => setStatus("picked_up")}
                  variant="outline"
                  className="w-full gap-1.5 rounded-full"
                  data-testid="confirm-arrived-fallback"
                >
                  Je suis arrivé chez le client
                </Button>
                {/* No-show workflow — le chauffeur peut déclarer le client
                    absent après `no_show_wait_minutes` d'attente sur place. */}
                <NoShowRow ride={ride} onDone={() => { refresh(); nav("/dashboard/driver"); }} />
              </>
            )}
          </>
        )}

        {/* Step 3 — slide "Commencer la course" (when client is in the car). 
            Atomically: launches GPS to destination + flips status to in_progress.
            The client app receives a `trip_started` notification and the
            LiveMap auto-switches to the destination route + ETA. */}
        {ride.status === "picked_up" && (
          <SlideToConfirm
            label="Glisser pour commencer la course"
            variant="primary"
            onConfirm={startTripToDestination}
            testid="slide-start-trip"
          />
        )}

        {ride.status === "in_progress" && (
          <>
            {/* Step 4 — GPS destination fallback. The slide in step 3 already
                launched the GPS, but if the driver dismissed the Maps app
                (or the popup blocker intercepted the deep-link), they can
                re-launch it here. */}
            <Button
              onClick={() => launchGoogleMapsTo(ride.arrival_address, ride.arrival_coord || ride.destination_coord)}
              className="w-full gap-1.5 rounded-full bg-primary hover:bg-primary/90"
              data-testid="active-gps-destination"
            >
              <Navigation className="h-4 w-4" /> GPS destination
            </Button>
            {/* Step 5 — slide "Terminer la course". Only gated on payment now;
                the driver can end the ride manually even if our GPS dropoff
                detection has not triggered (poor signal, indoor pickup,
                etc.). */}
            <SlideToConfirm
              label={!paid ? "Confirmez le paiement d'abord" : "Glisser pour terminer la course"}
              variant="success"
              disabled={!paid}
              onConfirm={() => setStatus("completed")}
              testid="slide-end-trip"
            />
            {/* Manual fallback — same action as the slide, for drivers who
                prefer a single tap (or struggle with the swipe gesture on
                small / wet screens). */}
            <Button
              onClick={() => setStatus("completed")}
              disabled={!paid}
              variant="outline"
              className="w-full gap-1.5 rounded-full border-[hsl(var(--success))]/50 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10 hover:text-[hsl(var(--success))] disabled:opacity-50"
              data-testid="active-end-trip-button"
            >
              <Flag className="h-4 w-4" /> Terminer la course
            </Button>
          </>
        )}
      </div>

      {/* Secondary actions */}
      <div className="mt-3 flex flex-wrap gap-2">
        {(ride.status === "picked_up" || ride.status === "in_progress") && !paid && !ride.is_multi_stop && (
          <Button
            onClick={() => setModifyDestOpen(true)}
            variant="outline"
            className="rounded-full gap-1.5 border-primary/40 text-primary hover:bg-primary/5"
            data-testid="active-modify-destination"
          >
            <Edit3 className="h-4 w-4" /> Modifier destination
          </Button>
        )}
        {ride.client_phone && (
          <Button asChild variant="outline" className="rounded-full gap-1.5" data-testid="active-call-client">
            <a href={`tel:${ride.client_phone}`}><Phone className="h-4 w-4" /> Appeler le client</a>
          </Button>
        )}
        {ride.status === "accepted" && (
          <Button
            variant="outline"
            onClick={() => setConfirmRelease(true)}
            className="rounded-full gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive ml-auto"
            data-testid="active-release"
          >
            <XCircle className="h-4 w-4" /> Annuler la course
          </Button>
        )}
      </div>

      {/* Chat — always visible on the active ride page */}
      <div className="mt-6">
        <ChatBox token={ride.tracking_token} author="driver" label={driverName} />
      </div>

      <Dialog open={confirmRelease} onOpenChange={(o) => !releasing && setConfirmRelease(o)}>
        <DialogContent data-testid="release-dialog">
          <DialogHeader>
            <DialogTitle>Annuler cette course ?</DialogTitle>
            <DialogDescription>
              La course sera remise à disposition des autres chauffeurs proches. L'hôtel garde sa réservation tant qu'il ne l'annule pas lui-même. Vous ne verrez plus cette course.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRelease(false)} disabled={releasing}>Garder la course</Button>
            <Button variant="destructive" onClick={releaseRide} disabled={releasing} data-testid="release-confirm">
              {releasing ? "Annulation…" : "Confirmer l'annulation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ModifyDestinationDialog
        open={modifyDestOpen}
        onOpenChange={setModifyDestOpen}
        bookingId={ride.id}
        currentAddress={ride.arrival_address}
        onSaved={refresh}
      />

      <AlertDialog open={cashConfirmOpen} onOpenChange={setCashConfirmOpen}>
        <AlertDialogContent data-testid="cash-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Avez-vous bien reçu le paiement ?</AlertDialogTitle>
            <AlertDialogDescription>
              {ride?.price ? `${ride.price} € ` : ""}
              Confirmez seulement après avoir reçu la totalité du paiement
              (espèces ou carte sur votre terminal). Cette action déverrouille
              le bouton « Terminer la course ».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cash-confirm-cancel">Pas encore</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCashConfirmed}
              className="bg-[hsl(var(--warning))] text-white hover:bg-[hsl(var(--warning))]/90"
              data-testid="cash-confirm-yes"
            >
              Oui, j&apos;ai reçu le paiement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/**
 * NoShowRow — bloc "Client absent" sur l'écran active-ride.
 *
 * Flow :
 *   1. Chauffeur clique "Client absent ?" → POST `/driver/bookings/{id}/arrived`
 *      qui marque `driver_arrived_at` et démarre le compte à rebours.
 *   2. Bouton devient "Attendre encore Xmin Ys" (désactivé) pendant la
 *      fenêtre `no_show_wait_minutes` (fetchée depuis platform_settings).
 *   3. Une fois écoulé → bouton "Déclarer client absent" actif → POST
 *      `/driver/bookings/{id}/no-show` → frais fixe capturé, course annulée.
 */
function NoShowRow({ ride, onDone }) {
  const [waitMin, setWaitMin] = React.useState(5);
  const [now, setNow] = React.useState(Date.now());
  const [busy, setBusy] = React.useState(false);
  const arrivedAt = ride?.driver_arrived_at ? new Date(ride.driver_arrived_at).getTime() : null;

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/platform-settings");
        if (alive && Number.isFinite(Number(data?.no_show_wait_minutes))) {
          setWaitMin(Number(data.no_show_wait_minutes));
        }
      } catch (_) { /* garde le défaut 5 min */ }
    })();
    return () => { alive = false; };
  }, []);

  React.useEffect(() => {
    if (!arrivedAt) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [arrivedAt]);

  const markArrived = async () => {
    setBusy(true);
    try {
      await api.post(`/driver/bookings/${ride.id}/arrived`);
      // On force un refresh depuis parent — l'active ride récupérera
      // `driver_arrived_at` au prochain tick.
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e) || "Impossible de marquer votre arrivée");
    } finally {
      setBusy(false);
    }
  };

  const declareNoShow = async () => {
    if (!window.confirm("Déclarer un no-show ? La course sera annulée et le frais fixe capturé sur la carte du client.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/bookings/${ride.id}/no-show`);
      const fee = Number(data?.cancellation_fee) || 0;
      toast.success(fee > 0 ? `No-show enregistré — ${fee.toFixed(2)} € facturés` : "No-show enregistré");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Déclaration impossible");
    } finally {
      setBusy(false);
    }
  };

  if (!arrivedAt) {
    return (
      <button
        type="button"
        onClick={markArrived}
        disabled={busy}
        className="flex w-full items-center justify-center gap-1.5 rounded-full border border-destructive/25 bg-destructive/5 px-3 py-2 text-xs font-medium text-destructive transition hover:bg-destructive/10 disabled:opacity-60"
        data-testid="driver-noshow-mark-arrived"
      >
        <UserX className="h-3.5 w-3.5" /> Client absent ? Je suis sur place
      </button>
    );
  }

  const elapsedSec = Math.max(0, Math.floor((now - arrivedAt) / 1000));
  const waitSec = waitMin * 60;
  const remainingSec = Math.max(0, waitSec - elapsedSec);
  const canDeclare = remainingSec === 0;

  return (
    <button
      type="button"
      onClick={declareNoShow}
      disabled={!canDeclare || busy}
      className={
        "flex w-full items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium transition " +
        (canDeclare
          ? "border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90"
          : "border-destructive/20 bg-destructive/5 text-destructive/70")
      }
      data-testid="driver-noshow-declare"
    >
      <UserX className="h-3.5 w-3.5" />
      {canDeclare
        ? "Déclarer client absent (no-show)"
        : `Attente ${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, "0")} avant no-show`}
    </button>
  );
}
